// src/components/games/crossroads/CardTooltip.tsx
// Tooltip for showing card details on hover/long-press

import React, { useState, useRef, useEffect } from 'react';
import { IonCard, IonCardContent, IonText, IonBadge } from '@ionic/react';
import { Card, CreatureCard, AbilityKeyword } from './types';
import './CardTooltip.css';

interface CardTooltipProps {
  card: Card;
  children: React.ReactNode;
  disabled?: boolean;
}

const KEYWORD_EXPLANATIONS: { [key in AbilityKeyword]: string } = {
  [AbilityKeyword.FLYING]: 'Can only be blocked by creatures with Flying or Reach',
  [AbilityKeyword.BLOCK]: 'Can block attackers',
  [AbilityKeyword.FIRST_STRIKE]: 'Deals combat damage before creatures without First Strike',
  [AbilityKeyword.DOUBLE_STRIKE]: 'Deals both First Strike and regular combat damage',
  [AbilityKeyword.DEATHTOUCH]: 'Any amount of damage this deals to a creature destroys it',
  [AbilityKeyword.LIFELINK]: 'Damage dealt also heals your commander',
  [AbilityKeyword.VIGILANCE]: "Doesn't tap when attacking",
  [AbilityKeyword.TRAMPLE]: 'Excess damage is dealt to the defending player',
  [AbilityKeyword.HASTE]: 'Can attack and tap immediately (no summoning sickness)',
  [AbilityKeyword.DEFENDER]: 'Cannot attack',
  [AbilityKeyword.REACH]: 'Can block creatures with Flying',
};

export const CardTooltip: React.FC<CardTooltipProps> = ({ card, children, disabled }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const childRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (disabled) return;
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!showTooltip) return;
    setPosition({ x: e.clientX + 15, y: e.clientY + 15 });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    const timer = setTimeout(() => {
      const touch = e.touches[0];
      setPosition({ x: touch.clientX + 15, y: touch.clientY - 100 });
      setShowTooltip(true);
    }, 500); // 500ms long press
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTimeout(() => setShowTooltip(false), 2000); // Hide after 2s on mobile
  };

  const isCreature = card.type === 'creature';
  const creatureCard = isCreature ? (card as CreatureCard) : null;

  return (
    <>
      <div
        ref={childRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ display: 'inline-block' }}
      >
        {children}
      </div>

      {showTooltip && (
        <div
          className="card-tooltip-overlay"
          style={{
            position: 'fixed',
            left: `${position.x}px`,
            top: `${position.y}px`,
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          <IonCard className="card-tooltip-card">
            <IonCardContent>
              {/* Card Name and Type */}
              <div className="card-tooltip-header">
                <IonText>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '1em', fontWeight: 700 }}>
                    {card.name}
                  </h3>
                </IonText>
                <IonBadge color="primary">{card.type}</IonBadge>
              </div>

              {/* Creature Stats */}
              {creatureCard && (
                <div className="card-tooltip-stats">
                  <IonBadge color="danger">⚔️ {creatureCard.power}</IonBadge>
                  <IonBadge color="success">❤️ {creatureCard.health}</IonBadge>
                  {creatureCard.elementType && (
                    <IonBadge color="medium">{creatureCard.elementType}</IonBadge>
                  )}
                </div>
              )}

              {/* Keywords */}
              {creatureCard?.keywords && creatureCard.keywords.length > 0 && (
                <div className="card-tooltip-keywords">
                  <IonText color="medium">
                    <strong style={{ fontSize: '0.85em' }}>Keywords:</strong>
                  </IonText>
                  {creatureCard.keywords.map((keyword) => (
                    <div key={keyword} className="keyword-explanation">
                      <IonBadge color="secondary" style={{ fontSize: '0.8em' }}>
                        {keyword}
                      </IonBadge>
                      <IonText color="medium">
                        <small>{KEYWORD_EXPLANATIONS[keyword]}</small>
                      </IonText>
                    </div>
                  ))}
                </div>
              )}

              {/* Abilities */}
              {'abilities' in card && card.abilities && card.abilities.length > 0 && (
                <div className="card-tooltip-abilities">
                  <IonText color="medium">
                    <strong style={{ fontSize: '0.85em' }}>Abilities:</strong>
                  </IonText>
                  {card.abilities.map((ability, idx) => (
                    <div key={idx} className="ability-text">
                      <IonText>
                        <small>{ability.description}</small>
                      </IonText>
                    </div>
                  ))}
                </div>
              )}

              {/* Flavor Text */}
              {card.flavorText && (
                <div className="card-tooltip-flavor">
                  <IonText color="medium">
                    <em style={{ fontSize: '0.85em' }}>{card.flavorText}</em>
                  </IonText>
                </div>
              )}
            </IonCardContent>
          </IonCard>
        </div>
      )}
    </>
  );
};

export default CardTooltip;
